import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { cloneDeep } from 'lodash-es';
import { Form } from '@common/formly/form.decorator';
import { filterStringPropertiesByValue } from '@common/utils/array-utils';
import { CONFIRMER, Confirmer } from '@shared/components/dialogs/confirm/types';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { EGI_APP_ACTIONS, EgiAppActions } from '@/app/platform/platform-data/actions/types';

@Component({
    selector: 'app-egi-app-list',
    templateUrl: './egi-app-list.component.html',
    styleUrls: ['./egi-app-list.component.css'],
    host: {
        class: 'fill-height'
    },
    standalone: false
})
export class EgiAppListComponent {
  @Output() select = new EventEmitter<EgiAppEntity>();

  @Input('active-app-id') set activeAppInput(v: string) {
    if (v) {
      this.activeAppId = v;
    }
  }

  @Input() egiApps: EgiAppEntity[];

  constructor(
    @Inject(EGI_APP_ACTIONS)
    private readonly egiAppActions: EgiAppActions,
    @Inject(CONFIRMER)
    private readonly confirmer: Confirmer
  ) {}

  searchText = '';
  activeAppId: string;
  editDialogOpened = false;
  createDialogOpened = false;
  appToEdit?: EgiAppEditFormEntity; // Check end of the file 3
  newApp = EgiAppEntity.createDefault();

  isCreating = false;
  isUpdating = false;

  get filteredEgiApps(): EgiAppEntity[] {
    return filterStringPropertiesByValue(this.egiApps, this.searchText);
  }

  closeEditDialog() {
    this.editDialogOpened = false;
  }

  openEditDialog(app: EgiAppEntity): void {
    this.appToEdit = EgiAppEditFormEntity.fromEgiApp(app);
    this.editDialogOpened = true;
  }

  openCreateDialog() {
    this.createDialogOpened = true;
    this.newApp = EgiAppEntity.createDefault();
  }

  closeCreateDialog() {
    this.createDialogOpened = false;
  }

  onDrop(e: CdkDragDrop<any>) {
    moveItemInArray(this.egiApps, e.previousIndex, e.currentIndex);
    const app = cloneDeep(this.egiApps[e.currentIndex]);
    app.changeOrder(e.currentIndex);

    this.egiAppActions.reOrder(app);
  }

  async deleteApp(app: EgiAppEntity) {
    const confirmation = await this.confirmer.confirm(
      'Ben je zeker dat je deze app wil verwijderen?'
    );
    if (confirmation) {
      const result = await this.egiAppActions.delete(app);
    }
  }

  async createApp(app: EgiAppEntity) {
    this.isCreating = true;
    const result = await this.egiAppActions.create(app);
    this.isCreating = false;
    result.ifSuccess(a => {
      this.closeCreateDialog();
      this.select.emit(a);
    });
  }

  async updateApp(app: EgiAppEditFormEntity) {
    this.isUpdating = true;
    const result = await this.egiAppActions.update(app.toEgiApp());
    this.isUpdating = false;

    const d = result.ifSuccess(r => {
      this.editDialogOpened = false;
    });
  }
}

// For some reason, passing an app to edit in the formly-metadata-component, throws stackoverflow errors, this is an issue in ngx-formly
@Form('edit', [
  {
    fieldGroupClassName: 'row',
    fieldGroup: [
      {
        className: 'col-6',
        key: 'name',
        type: 'input',
        templateOptions: {
          label: 'Naam',
          required: true,
          maxLength: 32
        },
        focus: true
      },
      {
        className: 'col-6',
        key: 'rootNavigationItemName',
        type: 'input',
        templateOptions: {
          required: true,
          label: 'Weergave in menu',
          maxLength: 32
        }
      }
    ]
  },
  {
    className: 'mt-5',
    type: 'textarea',
    key: 'info',
    templateOptions: {
      required: false,
      label: 'Beschrijving'
    }
  },
  {
    type: 'egi-page-select',
    key: 'pageComponentId',
    templateOptions: {
      required: true,
      label: 'Home pagina'
    }
  }
])
class EgiAppEditFormEntity {
  constructor(
    public id: string,
    public name: string,
    public rootNavigationItemName,
    public poolName: string,
    public machineName: string,
    public pageComponentId,
    public info: string
  ) {}

  static fromEgiApp(a: EgiAppEntity) {
    return new EgiAppEditFormEntity(
      a.id,
      a.name,
      a.rootNavigationItem.name,
      '',
      '',
      a.rootNavigationItem.pageComponentId,
      a.info
    );
  }

  toEgiApp(): EgiAppEntity {
    const a = EgiAppEntity.createWithId(this.id);
    a.name = this.name;
    a.rootNavigationItem.name = this.rootNavigationItemName;
    a.poolName = '';
    a.machineName = '';
    a.rootNavigationItem.pageComponentId = this.pageComponentId;
    a.info = this.info;

    return a;
  }
}
